import type { PromoBannerConfig, StorePromotionsPayload } from '@vastra/types';

/** Fallback when constants aren’t imported (keep in sync with MAX_ACTIVE_PROMO_BANNERS). */
const DEFAULT_MAX_ACTIVE = 2;

function isPromoWindowActive(startsAt?: string, endsAt?: string): boolean {
  const now = Date.now();
  if (startsAt) {
    const t = Date.parse(startsAt);
    if (Number.isFinite(t) && now < t) return false;
  }
  if (endsAt) {
    const t = Date.parse(endsAt);
    if (Number.isFinite(t) && now > t) return false;
  }
  return true;
}

/**
 * Normalize legacy `banner` + `banners` into a single list (order preserved).
 */
export function normalizePromoBanners(
  payload: Pick<StorePromotionsPayload, 'banner' | 'banners'> | null | undefined,
): PromoBannerConfig[] {
  if (Array.isArray(payload?.banners) && payload.banners.length > 0) {
    return payload.banners.filter(Boolean);
  }
  if (payload?.banner) return [payload.banner];
  return [];
}

export function resolveMaxActiveBanners(max?: number): 1 | 2 {
  return max === 1 ? 1 : 2;
}

/**
 * Determines whether a banner should be displayed on a given storefront route.
 * Supports both single `pageTarget` and multi-select `pageTargets`.
 */
export function isBannerActiveOnPage(banner: PromoBannerConfig, pathname?: string): boolean {
  if (!pathname) return true;

  const targets =
    Array.isArray(banner.pageTargets) && banner.pageTargets.length > 0
      ? banner.pageTargets
      : [banner.pageTarget || 'all'];

  if (targets.includes('all')) return true;

  const normalized = (pathname || '/').toLowerCase().replace(/\/$/, '') || '/';

  for (const target of targets) {
    switch (target) {
      case 'home':
        if (normalized === '/' || normalized === '') return true;
        break;
      case 'products':
        if (
          normalized === '/products' ||
          normalized.startsWith('/products/') ||
          normalized.startsWith('/product/')
        ) {
          return true;
        }
        break;
      case 'cart':
        if (normalized === '/cart') return true;
        break;
      case 'checkout':
        if (normalized === '/checkout' || normalized.startsWith('/checkout/')) return true;
        break;
      case 'blog':
        if (normalized === '/blog' || normalized.startsWith('/blog/')) return true;
        break;
      case 'custom': {
        const custom = (banner.targetPagePath || '').trim().toLowerCase();
        if (!custom) return true;
        const cleanCustom = custom.replace(/\/$/, '') || '/';
        if (
          normalized === cleanCustom ||
          normalized.startsWith(cleanCustom + '/') ||
          normalized.startsWith(cleanCustom + '?')
        ) {
          return true;
        }
        break;
      }
      default:
        break;
    }
  }

  return false;
}

/**
 * Banners that are enabled, have a message, fall inside their schedule window,
 * and match the targeted page path (if provided).
 * Sorted by startsAt (soonest/undefined first), then capped to `maxActive`.
 */
export function selectActivePromoBanners(
  banners: PromoBannerConfig[],
  maxActive: number = DEFAULT_MAX_ACTIVE,
  pathname?: string,
): PromoBannerConfig[] {
  const max = resolveMaxActiveBanners(maxActive);
  const live = banners.filter((b) => {
    if (!b?.enabled) return false;
    const text = (b.message || '').replace(/<[^>]+>/g, '').trim();
    if (!text) return false;
    if (!isPromoWindowActive(b.startsAt, b.endsAt)) return false;
    if (pathname && !isBannerActiveOnPage(b, pathname)) return false;
    return true;
  });

  live.sort((a, b) => {
    const ta = a.startsAt ? Date.parse(a.startsAt) : 0;
    const tb = b.startsAt ? Date.parse(b.startsAt) : 0;
    return ta - tb;
  });

  return live.slice(0, max);
}
